import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowLeft, ChevronDown, ChevronUp, HelpCircle, Shirt, Users, CreditCard, Shield, Zap, Camera } from 'lucide-react';
import Footer from '../components/Footer';

const FAQPage = () => {
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState('general');
  const [openQuestions, setOpenQuestions] = useState({});

  const toggleQuestion = (id) => {
    setOpenQuestions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const faqSections = [
    {
      id: 'general',
      title: 'General',
      icon: HelpCircle,
      questions: [
        {
          q: 'What is VirtualFit?',
          a: 'VirtualFit is an AI-powered virtual try-on platform that lets you see how clothes look on you before buying. Upload your photo and any clothing image, and our AI generates a realistic visualization of you wearing that outfit.'
        },
        {
          q: 'How does the AI try-on work?',
          a: 'Our AI analyzes your photo and the clothing image, then generates a new image showing you wearing the selected outfit. The technology preserves your face, body shape, and pose while realistically applying the clothing to your image.'
        },
        {
          q: 'Is VirtualFit free to use?',
          a: 'We offer 2 free try-on images for new users to experience the platform. After that, you can subscribe to one of our plans starting at $9/month for personal use, or explore our Brand and Influencer plans for business use.'
        },
        {
          q: 'What types of clothing work best?',
          a: 'VirtualFit works best with clear, front-facing clothing images on a plain background. Tops, dresses, shirts, and jackets typically produce the best results. Complex patterns and accessories may vary in accuracy.'
        },
        {
          q: 'How accurate are the generated images?',
          a: 'Our AI produces highly realistic results, but they are approximations. The generated images are meant to give you a good idea of how clothing might look on you, but actual fit and appearance may vary slightly from the real product.'
        }
      ]
    },
    {
      id: 'tryon',
      title: 'Virtual Try-On',
      icon: Camera,
      questions: [
        {
          q: 'What kind of photos should I upload?',
          a: 'For best results, upload a clear, well-lit photo of yourself facing the camera. Full-body or upper-body shots work best. Avoid photos with complex backgrounds, multiple people, or heavy filters.'
        },
        {
          q: 'Can I try on multiple items at once?',
          a: 'Currently, each try-on generates one clothing item at a time. You can create multiple try-ons with different items and compare the results.'
        },
        {
          q: 'What do the size options mean?',
          a: 'The size options (XS to XXL) tell the AI how fitted or loose the clothing should appear. Select your typical size for the most realistic representation of how the garment would fit you.'
        },
        {
          q: 'Why do generated images have watermarks?',
          a: 'AI-generated images include a "VirtualFit" watermark to identify them as AI-created content and protect intellectual property. This is standard practice for AI-generated imagery.'
        },
        {
          q: 'Can I download the generated images?',
          a: 'Yes! All generated images can be downloaded. Click the download button after your image is generated to save it to your device.'
        }
      ]
    },
    {
      id: 'brands',
      title: 'For Brands',
      icon: Shirt,
      questions: [
        {
          q: 'How can VirtualFit help my brand?',
          a: 'VirtualFit helps reduce returns by letting customers visualize products before purchase. Brands report up to 40-50% reduction in returns and significant savings on shipping costs. You can also collaborate with influencers to create engaging content.'
        },
        {
          q: 'What is a Brand Portfolio?',
          a: 'Your Brand Portfolio is where you upload your clothing items. Each item can include images, descriptions, pricing, and links. Customers and influencers can use these items for virtual try-ons.'
        },
        {
          q: 'How do campaigns work?',
          a: 'Brands can create paid campaigns to collaborate with influencers. Set a budget, define requirements, and invite influencers to create virtual try-on content featuring your products. You approve content before payment is released.'
        },
        {
          q: "What's the difference between Starter and Enterprise plans?",
          a: 'Brand Growth ($99/mo) includes 200 AI images, 25 portfolio items, and 2 influencer connections. Brand Starter ($199/mo) includes 500 AI images, 50 portfolio items, and 5 influencer connections. Brand Enterprise ($399/mo) offers 1000 images, 500 portfolio items, unlimited influencer connections, and priority support.'
        },
        {
          q: 'Can I add affiliate/product links to my items?',
          a: 'Yes! Each portfolio item can include a product URL. When users try on your items, they can click through to purchase directly from your store.'
        }
      ]
    },
    {
      id: 'influencers',
      title: 'For Influencers',
      icon: Users,
      questions: [
        {
          q: 'How can I earn money on VirtualFit?',
          a: 'Influencers can earn through paid brand campaigns. Brands pay you directly for creating virtual try-on content featuring their products. You can also add your own store/affiliate links to earn commissions on sales.'
        },
        {
          q: 'What is an Influencer Portfolio?',
          a: 'Your portfolio showcases your best virtual try-on looks. Upload images of yourself in different outfits to attract brand collaborations and grow your following.'
        },
        {
          q: 'How do I get brand collaborations?',
          a: 'Build an attractive portfolio, maintain an active profile, and brands will find you through our search. Pro plan members can also send collaboration requests directly to brands and get priority listing in search results.'
        },
        {
          q: "What's the difference between Starter and Pro plans?",
          a: 'Influencer Starter ($19/mo) includes 50 AI images and 20 portfolio items. Influencer Pro ($79.99/mo) offers 200 images, 100 portfolio items, ability to send collab requests, and priority listing in brand searches.'
        },
        {
          q: 'Can I add my Instagram profile?',
          a: 'Yes! You can add both your store/affiliate link and Instagram profile link in the "My Links" section of your dashboard. These will appear on your public profile for brands and followers to find you.'
        }
      ]
    },
    {
      id: 'payments',
      title: 'Payments & Billing',
      icon: CreditCard,
      questions: [
        {
          q: 'What payment methods do you accept?',
          a: 'We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment processor, Stripe.'
        },
        {
          q: 'How does billing work?',
          a: 'Subscriptions are billed monthly on the date you signed up. Your plan renews automatically until cancelled. You can cancel anytime from your account settings.'
        },
        {
          q: 'Can I change my plan?',
          a: "Yes! You can upgrade or downgrade your plan at any time. When upgrading, you'll be charged the prorated difference. When downgrading, the change takes effect at your next billing date."
        },
        {
          q: 'What happens if I cancel?',
          a: "When you cancel, you'll continue to have access to your plan until the end of your current billing period. After that, your account will revert to limited access."
        },
        {
          q: 'How do campaign payments work for influencers?',
          a: 'When you complete a campaign, the brand reviews and approves your content. Once approved, payment is released to your connected Stripe account. You can track earnings and withdraw funds from your dashboard.'
        },
        {
          q: 'Are there any refunds?',
          a: 'Subscription fees are generally non-refundable. However, if you experience technical issues that prevent you from using the service, please contact our support team.'
        }
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy & Security',
      icon: Shield,
      questions: [
        {
          q: 'Are my photos safe?',
          a: 'Yes. Your photos are processed securely and stored on Cloudinary, a trusted cloud service. We do not share your personal photos with third parties or use them to train AI models.'
        },
        {
          q: 'How long do you keep my data?',
          a: 'We retain your data for as long as your account is active. When you delete your account, all your data including uploaded images are permanently deleted from our systems and cloud storage.'
        },
        {
          q: 'Can I delete my account?',
          a: 'Yes. You can request account deletion by contacting our support team. We will delete all your personal data, images, and account information within 30 days as required by GDPR/CCPA.'
        },
        {
          q: 'Is my payment information secure?',
          a: 'Absolutely. We use Stripe for payment processing, which is PCI-DSS compliant. We never store your full credit card details on our servers.'
        },
        {
          q: 'Do you use cookies?',
          a: 'Yes, we use cookies to maintain your login session, track free trial usage, and improve our service. See our Privacy Policy for full details.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-700 hover:text-rose-500 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back to Home</span>
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <Sparkles className="w-7 h-7 text-rose-400" />
            <span className="text-xl font-bold bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">
              VirtualFit
            </span>
          </button>
          
          <div className="w-24"></div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-rose-400 to-orange-400 rounded-full mb-6">
            <HelpCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about VirtualFit. Can't find what you're looking for? 
            <a href="mailto:support@virtualfit.work" className="text-rose-500 hover:text-rose-600 ml-1">Contact our support team</a>.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Section Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-lg p-4 sticky top-24">
              <h3 className="font-semibold text-gray-800 mb-4 px-2">Categories</h3>
              <nav className="space-y-1">
                {faqSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setOpenSection(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                        openSection === section.id
                          ? 'bg-rose-50 text-rose-600'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{section.title}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* FAQ Content */}
          <div className="flex-1">
            {faqSections.map((section) => (
              <div
                key={section.id}
                className={openSection === section.id ? 'block' : 'hidden'}
              >
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-rose-400 to-orange-400 rounded-xl flex items-center justify-center">
                        <section.icon className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-800">{section.title}</h2>
                    </div>
                  </div>
                  
                  <div className="divide-y divide-gray-100">
                    {section.questions.map((item, index) => {
                      const questionId = `${section.id}-${index}`;
                      const isOpen = openQuestions[questionId];
                      
                      return (
                        <div key={index} className="group">
                          <button
                            onClick={() => toggleQuestion(questionId)}
                            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                            data-testid={`faq-question-${questionId}`}
                          >
                            <span className="font-medium text-gray-800 pr-4">{item.q}</span>
                            {isOpen ? (
                              <ChevronUp className="w-5 h-5 text-rose-500 flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-rose-500 flex-shrink-0" />
                            )}
                          </button>
                          
                          {isOpen && (
                            <div className="px-6 pb-4">
                              <p className="text-gray-600 leading-relaxed">{item.a}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-rose-500 to-orange-500 rounded-2xl p-8 text-center text-white">
          <Zap className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <h3 className="text-2xl font-bold mb-2">Still have questions?</h3>
          <p className="opacity-90 mb-6">Our support team is here to help you get the most out of VirtualFit.</p>
          <a
            href="mailto:support@virtualfit.work"
            className="inline-flex items-center px-6 py-3 bg-white text-rose-500 rounded-xl font-semibold hover:shadow-lg transition-shadow"
          >
            Contact Support
          </a>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default FAQPage;
